import React from "react";
import { Container, Button } from "reactstrap";

interface Props { children: React.ReactNode }
interface State { error: Error | null }

// App-wide error boundary so a single render crash shows a recoverable message
// instead of a blank white screen (React unmounts the whole tree on an uncaught
// throw). Previously the app had none — one bad API response could blank a page.
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Uncaught render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Container className="py-5 text-center">
          <h4 className="mb-3">Something went wrong</h4>
          <p className="text-muted" style={{ fontSize: 14 }}>
            This page hit an unexpected error. Try reloading; if it persists, please let us know.
          </p>
          {/* Raw exception text is for developers only (logged to console in
              componentDidCatch); never surface it to end users in production. */}
          {process.env.NODE_ENV === "development" && (
            <p className="text-muted" style={{ fontSize: 12, opacity: 0.7 }}>
              {this.state.error.message}
            </p>
          )}
          <Button color="primary" onClick={() => window.location.reload()}>Reload</Button>
        </Container>
      );
    }
    return this.props.children;
  }
}
